import type { Middleware } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { type Socket, io } from 'socket.io-client';

import type {
  TSDChatUpdateWsResponse,
  TSDMessageCountResponse
} from '@/components/Shared/widgets/types/serviceDesk.type';
import { type DictTypes } from '@/constants';
import { setWsIsConnected } from '@/store/app.slice';
import { setUpdateDicts } from '@/store/dict.slice';
import { setShouldUpdateFiles } from '@/store/financingStep.slice';
import { setBadgeNewLeadsCount } from '@/store/leads.slice';
import {
  setClientInfoFullName,
  setClientPhoneVerified,
  setShouldUpdateRequest,
  setShouldUpdateRequestByBiometricWs
} from '@/store/newOrder.slice';
import {
  addNewNotification,
  setSystemNotifications
} from '@/store/notifications.slice';
import { setShouldUpdateResponses } from '@/store/offerStep.slice';
import { updateBadgeCount } from '@/store/security.slice';
import {
  updateServiceDeskBadges,
  updateServiceDeskNewRequestBadge,
  updateServiceDeskRequest,
  updateServiceDeskReworkRequestBadge
} from '@/store/serviceDesk.slice';
import type {
  SystemNotificationResponse,
  TNotificationServerResponse
} from '@/types/notifications';
import type { ClientServicePayload } from '@/types/socket.types';

import type { TRootState } from '../../store';
import { idleTracker } from './helpers/idle-tracker';

let socket: Socket | null = null;

export const wsMiddleware: Middleware = (storeAPI) => (next) => (action) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  const isConnected = (storeAPI.getState() as TRootState).app.wsIsConnected;
  const user = (storeAPI.getState() as TRootState).userInfo.userInfo;

  const idleListener = () => {
    if (socket) {
      socket.disconnect();
      socket = null;
      storeAPI.dispatch(setWsIsConnected(false));
    }
  };

  const activeListener = () => {
    if (!socket) connectSocket();
  };

  function connectSocket() {
    if (isAuthenticated && !socket && !isConnected && user?.uuid) {
      socket = io(process.env.REACT_APP_SOCKET_URL, {
        transports: ['websocket'],
        auth: { token: user }
      });
      idleTracker.registerIdleListener(idleListener);
      idleTracker.registerActiveListener(activeListener);
      idleTracker.startTracker();

      socket.on('connect', () => {
        storeAPI.dispatch(setWsIsConnected(true));
      });

      socket.on('connect_error', () => {
        storeAPI.dispatch(setWsIsConnected(false));
      });

      socket.on(
        'RequestsService_Updated',
        async (data: { payload: { request_uuid: string } }) => {
          if (data?.payload?.request_uuid) {
            storeAPI.dispatch(setShouldUpdateRequest(true));
          }
        }
      );

      socket.on(
        'ResponsesService_Updated',
        async (data: { payload: { response_uuid: string } }) => {
          if (data?.payload?.response_uuid) {
            storeAPI.dispatch(setShouldUpdateResponses(true));
          }
        }
      );

      socket.on('ClientsService', (data: { payload: ClientServicePayload }) => {
        const currentRequest = storeAPI.getState().newOrder.orderApp;
        if (currentRequest?.uuid === data.payload.request_id) {
          storeAPI.dispatch(
            setClientPhoneVerified(
              data.payload?.is_phone_number_verified ?? true
            )
          );
          if (data.payload.last_name && data.payload.first_name)
            storeAPI.dispatch(
              setClientInfoFullName({
                lastName: data.payload.last_name,
                firstName: data.payload.first_name,
                middleName: data.payload.middle_name || ''
              })
            );
          if (data.payload.is_phone_number_verified === false)
            notification.warning({
              title:
                'Предоставленный клиентом номер не совпал с указанным в сервисе БМГ по ИИНу и возможны отказы по некоторым банкам'
            });
        }
      });

      socket.on(
        'System_Notification',
        (data: { payload: SystemNotificationResponse[] }) => {
          storeAPI.dispatch(setSystemNotifications(data.payload));
        }
      );

      socket.on('Leads', (data: { payload: number[] }) => {
        storeAPI.dispatch(setBadgeNewLeadsCount(data.payload?.[0] || 0));
      });

      // Service Desk. Счетчик новых заявок в меню для админов
      socket.on('SupportsCount_Updated', (data: { payload: [number] }) => {
        storeAPI.dispatch(
          updateServiceDeskNewRequestBadge(data.payload?.[0] || 0)
        );
      });
      // Service Desk. Счетчик заявок со статусом "Возобновлен" в меню для админов
      socket.on(
        'ReworkSupportsCount_Updated',
        (data: { payload: [number] }) => {
          storeAPI.dispatch(
            updateServiceDeskReworkRequestBadge(data.payload?.[0] || 0)
          );
        }
      );

      // Service Desk. Новые сообщения по чату
      socket.on(
        'SupportsChat_Updated',
        (data: { payload: TSDChatUpdateWsResponse }) => {
          const store = storeAPI.getState() as TRootState;
          storeAPI.dispatch(
            updateServiceDeskRequest({ response: data.payload, store })
          );
        }
      );

      // Service Desk. Счетчик новых сообщений по чату для админов
      socket.on(
        'SupportsChatsAdminNewMessage_Updated',
        (data: { payload: TSDMessageCountResponse[] }) => {
          const store = storeAPI.getState() as TRootState;
          const userId = store.userInfo.userInfo?.uuid;
          const isAdmin = store.userInfo.userAccesses.global.isAdmin;
          if (userId && isAdmin) {
            storeAPI.dispatch(
              updateServiceDeskBadges(
                data.payload.filter((item) => item.support_user === userId)
              )
            );
          }
        }
      );

      // Service Desk. Счетчик новых сообщений по чату для юзеров
      socket.on(
        'SupportsChatsUserNewMessage_Updated',
        (data: { payload: TSDMessageCountResponse[] }) => {
          const store = storeAPI.getState() as TRootState;
          const userId = store.userInfo.userInfo?.uuid;
          const isAdmin = store.userInfo.userAccesses.global.isAdmin;
          if (userId && !isAdmin) {
            storeAPI.dispatch(
              updateServiceDeskBadges(
                data.payload.filter((item) => item.create_user === userId)
              )
            );
          }
        }
      );

      socket.on('SecurityCount_Updated', (data: { payload: [number] }) => {
        storeAPI.dispatch(updateBadgeCount(data.payload?.[0] || 0));
      });

      socket.on(
        'FlashLogsService_Updated',
        (data: { payload: TNotificationServerResponse }) => {
          storeAPI.dispatch(addNewNotification(data.payload));
        }
      );

      socket.on('Kestra_Notification_Updated', (data: { payload: string }) => {
        if (data.payload) {
          notification.success({ title: data.payload });
        }
      });

      socket.on(
        'FilesService_Updated',
        (data: { payload: { request_uuid: string } }) => {
          // const validMessage = serverNotificationsMapper(data);
          // store.dispatch(addNewNotification(validMessage));
          const selectedBank = (storeAPI.getState() as TRootState).financingStep
            .selectedBank;

          if (selectedBank && selectedBank.id === data.payload.request_uuid) {
            storeAPI.dispatch(setShouldUpdateFiles(true));
          }
        }
      );

      socket.on(
        'DictionariesService',
        (data: { payload: { dictName: DictTypes } }) => {
          storeAPI.dispatch(setUpdateDicts([data.payload.dictName]));
        }
      );

      socket.on(
        'BiometricsService:biometricVisionOfflineCallback_Updated',
        (data: {
          payload: {
            request_uuid: string;
            biometric_validated: boolean;
          };
        }) => {
          storeAPI.dispatch(setShouldUpdateRequestByBiometricWs(data.payload));
        }
      );

      socket.on(
        'GpFactoryCheckService_Updated',
        (data: { payload: { response_uuid: string } }) => {
          const { selectedBank } = (storeAPI.getState() as TRootState)
            .financingStep;

          if (selectedBank?.id === data.payload.response_uuid) {
            storeAPI.dispatch(setShouldUpdateResponses(true));
          }
        }
      );
    } else if (
      (!isAuthenticated ||
        (action as { type: string }).type === 'user/logout') &&
      socket
    ) {
      socket.disconnect();
      socket = null;
    }
  }

  connectSocket();

  return next(action);
};
